<template>
    <component
        :is="tag"
        :aria-busy="loading"
        :aria-describedby="ariaDescribedby"
        :aria-label="computedAriaLabel"
        :class="buttonClasses"
        :disabled="disabled || loading"
        :href="linkRoute"
        :target="link ? '_blank' : undefined"
        :type="tag === 'button' ? nativeType : undefined"
        @click="onClick"
    >
        <f-a-icon v-if="loading" class="size-3.5 animate-spin" name="spinner-third" />
        <!--
            FontAwesomeIcon sets `height: 1em` as an inline style on the rendered SVG,
            which overrides Tailwind size utilities. The wrapper span defines the intended
            icon size, and `[&>svg]:size-full!` forces the SVG to fill it — keeping the
            !important concern contained here rather than leaking it to consumers.
        -->
        <span
            v-else-if="$slots['icon-left']"
            class="shrink-0"
            :class="props.size === 'small' ? 'fa-md' : 'fa-lg'"
        >
            <slot name="icon-left" />
        </span>
        <span v-if="label" v-text="label" />
        <span
            v-if="$slots['icon-right']"
            class="shrink-0"
            :class="props.size === 'small' ? 'fa-md' : 'fa-lg'"
        >
            <slot name="icon-right" />
        </span>
    </component>
</template>

<script setup lang="ts">
import {computed, toRef} from 'vue';
import {type RouterLinkProps} from 'vue-router';
import useButtonLink from '../../../composables/useButtonLink';
import FAIcon from '../../icons/FAIcon.vue';
import {buttonBaseClasses, buttonVariantClasses} from './buttonSharedPt';

type Props = {
    variant?:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'textlink'
        | 'primary-danger'
        | 'tertiary-danger'
        | 'textlink-danger';
    size?: 'default' | 'small';
    label?: string | null;
    disabled?: boolean;
    nativeType?: 'button' | 'submit';
    to?: RouterLinkProps['to'] | null;
    link?: string;
    ariaLabel?: string;
    ariaDescribedby?: string;
    loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    variant: 'primary',
    size: 'default',
    label: null,
    nativeType: 'button',
    to: null,
    link: undefined,
    ariaLabel: undefined,
    ariaDescribedby: undefined,
});

const {route, onClick, as} = useButtonLink(toRef(() => props.to));

const tag = computed(() => (props.link ? 'a' : as.value || 'button'));

const linkRoute = computed(() => route.value?.href ?? props.link);

const computedAriaLabel = computed(() => props.ariaLabel || props.label || undefined);

const isTextlink = computed(
    () => props.variant === 'textlink' || props.variant === 'textlink-danger'
);

const buttonClasses = computed(() => [
    buttonBaseClasses,
    isTextlink.value ? 'h-fit' : props.size === 'small' ? 'h-7 px-3' : 'h-11 px-6',
    buttonVariantClasses[props.variant],
    props.disabled || props.loading
        ? 'opacity-40 pointer-events-none cursor-default'
        : 'cursor-pointer',
]);
</script>
